"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight, Clock, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqData = [{
  id: "item-1",
  question: "What are the early signs that my drains need professional cleaning?",
  answer: "Common early indicators include water draining slowly in sinks or tubs, gurgling sounds coming from pipes, unpleasant sewer odors near fixtures, and recurring standing water. If multiple fixtures back up simultaneously, it usually indicates a mainline clog that requires immediate professional attention."
}, {
  id: "item-2",
  question: "Why should I avoid chemical drain cleaners from the store?",
  answer: "Chemical drain cleaners contain harsh acids and caustic ingredients that generate extreme heat inside your pipes. Over time, these chemicals can corrode PVC, soften older metal pipes, crack porcelain, and cause dangerous splashback hazards without completely removing tough blockages like tree roots or grease."
}, {
  id: "item-3",
  question: "How do your technicians clear severe or recurring clogs?",
  answer: "We utilize professional-grade motorized drain augers (snakes) and high-pressure hydro-jetting equipment. We also deploy fiber-optic sewer camera inspections to accurately locate the exact depth and cause of the blockage, ensuring a comprehensive cleaning without guessing."
}, {
  id: "item-4",
  question: "How quickly can you arrive for emergency drain backups in Jonesboro?",
  answer: "We operate 24 hours a day, 7 days a week. For urgent drain emergencies such as sewer line overflows, backed-up toilets, or flooding kitchen sinks, our Jonesboro service vans are dispatched immediately to minimize property water damage."
}, {
  id: "item-5",
  question: "How often should residential sewer and drain lines be cleaned?",
  answer: "For most households, a preventative professional drain inspection and cleaning every 18 to 24 months keeps lines free of grease, soap scum, and mineral buildup. Homes with mature yard trees or older cast iron piping may benefit from annual preventative maintenance."
}];
export default function DrainFaq() {
  return <section id="draincleaningfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column: Heading and Fast Contact Card */}
          <div className="lg:col-span-5 flex flex-col space-y-6">
            <div>
              <Badge variant="secondary" className="mb-3.5 gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                <HelpCircle className="size-3.5 text-accent" />
                Clear Answers
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
                Drain Cleaning FAQs
              </h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                Got questions about clogged drains, sewer lines, or emergency pipe service? Here is what you need to know before our plumbers arrive.
              </p>
            </div>

            {/* Quick emergency contact highlight card */}
            <div className="bg-card text-card-foreground border border-border rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Clock className="size-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-card-foreground font-default">
                    Facing a Major Overflow?
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Available 24/7 across Jonesboro and surrounding areas.
                  </p>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-border space-y-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="size-4 text-accent shrink-0" />
                  <span>Licensed technicians with camera inspection equipment</span>
                </div>
                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="default" className="w-full justify-center gap-2 font-medium">
                    <a href="tel:4708479076">
                      <PhoneCall className="size-4" />
                      Call Emergency Dispatch
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="default" className="w-full justify-center gap-2">
                    <Link to="/contact/">
                      Schedule Online
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: 5 FAQ Accordion items */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
              {faqData.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="bg-card text-card-foreground border border-border rounded-xl px-5 transition-colors shadow-2xs data-[state=open]:border-primary/40">
                  <AccordionTrigger className="py-4 text-left text-base font-semibold text-card-foreground hover:no-underline hover:text-primary transition-colors">
                    <span className="pr-2">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground pb-5 pt-1">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}